import { workflowsApi } from '@/api/workflows';
import { liveTabIds } from '@/auth/tabRegistry';

/**
 * localStorage key recording which workflows are open for editing, and in which tabs.
 *
 * Shape: `{ [workflowId]: tabId[] }`. It used to be a single workflow id, which made the
 * lock a property of ONE tab: whichever tab last wrote the key owned it, and whichever tab
 * unmounted first released it. Two tabs on the same workflow therefore fought — closing the
 * second released the lock the first was actively using, and its next save 409'd.
 *
 * Tracking the holders as a set fixes that: the release only fires when the LAST tab holding
 * a workflow lets go. The ids are the same ones `tabRegistry` issues, so a tab that crashed
 * without running its cleanup is pruned by the same STALE_MS liveness rule that governs the
 * tab cap — otherwise a crash would pin a workflow lock until the server-side TTL.
 *
 * The key doubles as the AC08 resilience net it always was: if every tab dies without
 * releasing, the next visit to the Workflows landing page reads what is left and releases it.
 */
export const EDITING_WORKFLOW_STORAGE_KEY = 'bmo-inflow.editingWorkflowId';

type EditingRegistry = Record<string, string[]>;

/**
 * Read the registry, dropping tabs the slot registry no longer considers alive.
 *
 * Pruning on READ rather than on a timer means there is no bookkeeping to schedule and no
 * way for the two registries to drift: a tab is holding a lock exactly as long as
 * `tabRegistry` still believes that tab exists.
 */
function read(): EditingRegistry {
  let raw: string | null;
  try {
    raw = localStorage.getItem(EDITING_WORKFLOW_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, quota). An empty registry is the safe reading —
    // we lose the proactive release and fall back to the server-side TTL.
    return {};
  }
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Pre-migration value: the old shape was a bare workflow id string, not JSON. Treat it as
    // "this workflow is open in a tab we can no longer identify" so the landing-page cleanup
    // still releases it once, rather than leaking the lock across the deploy.
    return raw ? { [raw]: [] } : {};
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const live = new Set(liveTabIds());
  const out: EditingRegistry = {};
  for (const [workflowId, tabs] of Object.entries(parsed as Record<string, unknown>)) {
    if (!workflowId) continue;
    // A workflow with no live tabs still counts as held — it is exactly the orphan the
    // landing page exists to clean up. Only the tab LIST is pruned here.
    out[workflowId] = Array.isArray(tabs)
      ? tabs.filter((t) => typeof t === 'string' && live.has(t))
      : [];
  }
  return out;
}

function write(registry: EditingRegistry): void {
  try {
    if (Object.keys(registry).length === 0) {
      localStorage.removeItem(EDITING_WORKFLOW_STORAGE_KEY);
      return;
    }
    localStorage.setItem(EDITING_WORKFLOW_STORAGE_KEY, JSON.stringify(registry));
  } catch {
    // See read() — a missed write just means we lean on the TTL. Never surface to the user.
  }
}

/** Record that `tabId` has `workflowId` open for editing. Idempotent. */
export function rememberEditingWorkflow(workflowId: string, tabId: string): void {
  const registry = read();
  const tabs = registry[workflowId] ?? [];
  if (!tabs.includes(tabId)) tabs.push(tabId);
  registry[workflowId] = tabs;
  write(registry);
}

/**
 * Drop `tabId`'s claim on `workflowId`, and report whether that was the LAST one.
 *
 * The boolean is the whole contract: the caller releases the server-side lock only when it
 * comes back true. A sibling tab still holding the workflow keeps the lock alive.
 */
export function forgetEditingWorkflow(workflowId: string, tabId: string): boolean {
  const registry = read();
  const remaining = (registry[workflowId] ?? []).filter((t) => t !== tabId);
  if (remaining.length > 0) {
    registry[workflowId] = remaining;
    write(registry);
    return false;
  }
  delete registry[workflowId];
  write(registry);
  return true;
}

/** Every workflow id currently recorded as open, orphans included. */
export function getRememberedEditingWorkflows(): string[] {
  return Object.keys(read());
}

/**
 * Release every workflow left in the registry. Called from the Workflows landing page on
 * mount (AC08) — returning to the list IS the act of leaving the editing session, so anything
 * still recorded at that point is finished with, whether it was left by a clean navigation
 * this tab missed or by a tab that died mid-edit.
 *
 * Entries are cleared only on a successful release: if the call fails (network blip, BE down)
 * the id stays put so the next landing-page visit retries, with the server-side TTL as the
 * ultimate backstop. Releases are idempotent on the BE, so retrying costs nothing.
 */
export async function releaseRememberedLockIfAny(): Promise<void> {
  const workflowIds = getRememberedEditingWorkflows();
  if (workflowIds.length === 0) return;

  await Promise.all(
    workflowIds.map(async (workflowId) => {
      try {
        await workflowsApi.releaseLock(workflowId);
        const registry = read();
        delete registry[workflowId];
        write(registry);
      } catch {
        // Retained for the next landing-page mount to retry.
      }
    }),
  );
}

/**
 * Forget every recorded workflow WITHOUT calling the BE.
 *
 * For sign-off, where `releaseAllMyLocks` has already cleared the server side in one call and
 * per-workflow releases would be redundant noise against a session that is being torn down.
 */
export function clearEditingRegistry(): void {
  write({});
}

/**
 * Hand back every lock this user holds, then forget the local registry. Call this on logout
 * and on session expiry — BEFORE the session is invalidated, while the cookie is still good.
 *
 * Without it, signing out mid-edit left the workflow locked to a session that no longer
 * exists for the rest of the TTL, with colleagues reading "currently being edited by" for
 * someone who had gone home. Route-unmount used to release it by accident, which failed in
 * exactly the case that matters: once the session is gone the release 401s.
 *
 * Best-effort by construction. Sign-off must proceed whether or not this succeeds, so the
 * failure path is a swallowed catch and the server-side TTL remains the backstop for the
 * cases no client code can cover (closed tab, crash, lost network).
 */
export async function releaseAllLocksForSignOff(): Promise<void> {
  try {
    await workflowsApi.releaseAllMyLocks();
  } catch {
    // Never block sign-off on lock cleanup.
  } finally {
    clearEditingRegistry();
  }
}
